/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { PayloadAction } from '@reduxjs/toolkit';
import {
  call, put, fork, delay, takeLatest,
} from 'redux-saga/effects';
import { API } from '../Utils/Constants';
import axios from '../Utils/Axios';
import { actions } from '../Slice/AdvanceSearchSlice';
import {
  PersonLinkedinSearchResponse,
} from '../Types/GlobalSearchResultsTypes';
import {
  AdvanceSearchRequest,
  AdvanceSearchResponse,
  AdvanceSearchMetadataResponse,
  AdvanceSearchDropdownOption,
} from '../Types/AdvanceSearchResultsTypes';
import { FollowedPeopleResponse } from '../Types/CompaniesTypes';
import { FollowedPeopleRequest } from '../Types/RequestBodies';
import { Executive } from '../Types/PersonTypes';
import { backgroundColorParsing } from '../Utils/UtilityFunctions';

const mapExecutivesToOptions = (executives: Executive[]): AdvanceSearchDropdownOption[] => executives
  .map((person) => ({
    id: person.userId ?? person.personId ?? person.feedid,
    label: person.personName ?? '',
    image: person.personImage ?? '',
    subtitle: person.companyName ?? '',
    personDesignation: person.personDesignation ?? '',
    personaColor: backgroundColorParsing(person.personalityTypes ?? []),
    personalityType: (person.personalityTypes ?? []).map((type) => type.value).filter(Boolean).join('/'),
  }))
  .filter((option) => option.id && option.label);

export function* advanceSearchSaga(dispatched: PayloadAction<AdvanceSearchRequest>) {
  try {
    const response: AdvanceSearchResponse = yield call(
      axios.post,
      API.ADVANCE_SEARCH,
      dispatched.payload,
    );
    if (response?.status === true) {
      yield put(actions.advanceSearchLoaded(response.searchResult));
    } else {
      yield put(actions.advanceSearchError(response?.message));
    }
  } catch (err) {
    console.log(err);
    yield put(actions.advanceSearchError('Connection Issue'));
  }
}

export function* advancePersonLinkedinSearch(dispatched: PayloadAction<AdvanceSearchRequest>) {
  try {
    const { page, ...data } = dispatched.payload;
    const response: PersonLinkedinSearchResponse = yield call(
      axios.post,
      API.GET_PERSON_EXTENDED_SEARCH,
      { ...data, defaultLanguage: 'en', advancedSearch: true },
    );
    if (response?.status === true) {
      yield put(actions.advanceSearchPersonExtendedLoaded(response.response));
    } else {
      yield put(actions.advanceSearchPersonExtendedDone());
    }
  } catch (err) {
    console.log(err);
    yield put(actions.advanceSearchPersonExtendedDone());
  }
}

export function* advanceSearchFlow(dispatched: PayloadAction<AdvanceSearchRequest>) {
  const isFirstPage = !dispatched.payload.page || dispatched.payload.page <= 1;
  yield fork(advanceSearchSaga, dispatched);
  if (isFirstPage) {
    if (dispatched.payload.searchType === 'person') {
      yield delay(2000);
      yield fork(advancePersonLinkedinSearch, dispatched);
    } else {
      yield put(actions.advanceSearchPersonExtendedDone());
    }
  }
}

export function* getAdvanceSearchMetadata() {
  try {
    const response: AdvanceSearchMetadataResponse = yield call(
      axios.get,
      API.ADVANCE_SEARCH_METADATA,
    );
    if (response?.status === true) {
      yield put(actions.advanceSearchMetadataLoaded(response.response.result));
    }
  } catch (err) {
    console.log(err);
  }
}

export function* getAdvanceSearchMembers(dispatched: PayloadAction<FollowedPeopleRequest>) {
  try {
    const payload = dispatched.payload;
    const data = {
      offset: payload.offset,
      ...payload.searchTerm && { query: payload.searchTerm },
    };
    const response: FollowedPeopleResponse = yield call(
      axios.post,
      API.GET_FOLLOWED_PEOPLE,
      data,
    );
    if (response?.status === true) {
      const pass = response?.response?.[0];
      const options = mapExecutivesToOptions(pass?.members ?? []);
      yield put(actions.setAdvanceSearchMembers({
        options,
        total: pass?.followedPeople ?? 0,
        offset: payload.offset,
      }));
    } else {
      yield put(actions.advanceSearchDropdownError());
    }
  } catch (err) {
    console.log(err);
    yield put(actions.advanceSearchDropdownError());
  }
}

export function* advanceSearchRootSaga() {
  yield takeLatest(actions.triggerAdvanceSearch.type, advanceSearchFlow);
  yield takeLatest(actions.getAdvanceSearchMetadata.type, getAdvanceSearchMetadata);
  yield takeLatest(actions.getAdvanceSearchMembers.type, getAdvanceSearchMembers);
}
